import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { EventService } from "../../services/event_service";
import config from "../../config/config";
import styles from "./EventDetails.module.css";
import {
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaMoneyBillWave
} from "react-icons/fa";
import { useEventDetailsState } from "../../states/useEventDetailState";
const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const eventService = EventService();
  const {
    event,
    setEvent,
    loading,
    setLoading,
    error,
    setError
  } = useEventDetailsState();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchEventDetails = async () => {
      try {
        const eventData = await eventService.fetchEventById(id);
        setEvent(eventData);
      } catch (error) {
        console.error("Error fetching event details", error);
        setError("Event not found");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchEventDetails();
    } else {
      setError("Event ID is missing");
      setLoading(false);
    }
  }, [id]);

  const getFullImageUrl = (imagePath) => {
    if (!imagePath) return "/default.png";
    if (imagePath.startsWith("http")) return imagePath;
    return `${config.baseURL}${imagePath}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Date not specified";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "Time not specified";
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getEventStatus = (event) => {
    if (!event || !event.start_datetime) return "unknown";
    const now = new Date();
    const start = new Date(event.start_datetime);
    const end = new Date(event.end_datetime);

    if (now < start) return "upcoming";
    if (now >= start && now <= end) return "ongoing";
    return "completed";
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Loading Event Details...</p>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorContent}>
          <h2>Event Not Found</h2>
          <p>The event you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate("/event")}
            className={styles.backButton}
          >
            Back to Events
          </button>
        </div>
      </div>
    );
  }

  const status = getEventStatus(event);

  return (
    <div className={styles.eventDetailsPage}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBackground}>
          {event.image && (
            <img
              src={getFullImageUrl(event.image)}
              alt={event.title}
              className={styles.heroImage}
            />
          )}
        </div>
        <button
          onClick={() => navigate("/event")}
          className={styles.backButton}
        >
          ← Back
        </button>
      </section>

      {/* Event Details */}
      <section className={styles.detailsSection}>
        <div className={styles.container}>
          <div className={styles.detailsGrid}>
            <div className={styles.mainContent}>
              <div className={styles.descriptionSection}>
                <h2>About This Event</h2>
                <h3>{event.title}</h3>
                {event.description ? (
                  <div
                    className={styles.eventDescription}
                    dangerouslySetInnerHTML={{ __html: event.description }}
                    style={{textAlign : "justify"}}
                  />
                ) : (
                  <p>No description available for this event.</p>
                )}
              </div>

              {/* Add other sections as needed */}
            </div>

            <div className={styles.sidebar}>
              <div className={styles.eventInfoCard}>
                <h3>Event Information</h3>

                <div className={styles.infoItem}>
                  <span className={styles.infoIcon}>
                    <FaCalendarAlt />
                  </span>

                  <div className={styles.infoContent}>
                    <div className={styles.infoLabel}>Date</div>
                    <div className={styles.infoValue}>
                      {event.start_datetime ? (
                        <>
                          {formatDate(event.start_datetime)} {formatTime(event.start_datetime)} -{" "}
                          {formatDate(event.end_datetime)} {formatTime(event.end_datetime)}
                        </>
                      ) : (
                        <span>Event Upcoming</span>
                      )}

                    </div>
                  </div>
                </div>

                <div className={styles.infoItem}>
                  <span className={styles.infoIcon}>
                    <FaMapMarkerAlt />
                  </span>
                  <div className={styles.infoContent}>
                    <div className={styles.infoLabel}>Location</div>
                    <div className={styles.infoValue}>
                      {event.location || "Not specified"}
                    </div>
                  </div>
                </div>

                {event.contact_no && (
                  <div className={styles.infoItem}>
                    <span className={styles.infoIcon}>
                      <FaPhoneAlt />
                    </span>
                    <div className={styles.infoContent}>
                      <div className={styles.infoLabel}>Contact Number</div>
                      <div className={styles.infoValue}>{event.contact_no}</div>
                    </div>
                  </div>
                )}

                {event.price && (
                  <div className={styles.infoItem}>
                    <span className={styles.infoIcon}>
                      <FaMoneyBillWave />
                    </span>
                    <div className={styles.infoContent}>
                      <div className={styles.infoLabel}>Price</div>
                      <div className={styles.infoValue}>{event.price}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EventDetails;
